import {
  RadioButtonUncheckedRounded as CircleIcon,
  HorizontalRuleRounded as LineIcon,
  ArrowRightAltRounded as ArrowIcon,
  CropSquareRounded as BoxIcon,
  HighlightRounded as HighlightIcon,
  ChatBubbleOutlineRounded as CommentIcon,
  MouseRounded as SelectIcon,
} from "@mui/icons-material";

// Variable categories based on actual Jinja template paths and aliases used in document generation
export const VARIABLE_CATEGORIES = {
  "Insured / Program": {
    expanded: true,
    items: [
      { path: "S.program.insured", label: "Insured Name", type: "string" },
      {
        path: "S.program.insuredAddress",
        label: "Insured Address",
        type: "string",
      },
      {
        path: "S.program.riskDescription",
        label: "Risk Description",
        type: "string",
      },
      { path: "S.program.riskSummary", label: "Risk Summary", type: "string" },
      {
        path: "S.program.reference",
        label: "Submission Reference",
        type: "string",
      },
      {
        path: "S.program.industryClass",
        label: "Industry Class",
        type: "string",
      },
      { path: "S.program.naics", label: "NAICS Code", type: "string" },
      { path: "S.program.currency", label: "Currency", type: "string" },
      {
        path: "S.program.transaction",
        label: "Transaction Type",
        type: "string",
      },
    ],
  },
  Dates: {
    expanded: true,
    items: [
      { path: "S.program.inception", label: "Inception Date", type: "date" },
      { path: "S.program.expiration", label: "Expiration Date", type: "date" },
      {
        path: "S.program.effectiveExpiration",
        label: "Effective Expiration Date",
        type: "date",
      },
      { path: "S.program.quoteDate", label: "Quote Date", type: "date" },
      {
        path: "S.program.quoteValidUntil",
        label: "Quote Valid Until",
        type: "date",
      },
      {
        path: "S.program.quoteDeadline",
        label: "Quote Deadline",
        type: "date",
      },
      { path: "S.program.dateReceived", label: "Date Received", type: "date" },
      { path: "today()", label: "Today's Date", type: "date" },
    ],
  },
  Broker: {
    expanded: false,
    items: [
      {
        path: "person(S, 'BROKER').name",
        label: "Broker Name",
        type: "string",
      },
      {
        path: "person(S, 'BROKER').company",
        label: "Broker Company",
        type: "string",
      },
      {
        path: "person(S, 'BROKER').email",
        label: "Broker Email",
        type: "string",
      },
      {
        path: "person(S, 'BROKER').location",
        label: "Broker Location",
        type: "string",
      },
      {
        path: "person(S, 'SURPLUS_BROKER').name",
        label: "Surplus Lines Broker Name",
        type: "string",
      },
      {
        path: "person(S, 'SURPLUS_BROKER').company",
        label: "Surplus Lines Broker Company",
        type: "string",
      },
      {
        path: "person(S, 'SURPLUS_BROKER').licenseNumber",
        label: "Surplus Lines Broker License",
        type: "string",
      },
    ],
  },
  Underwriter: {
    expanded: false,
    items: [
      {
        path: "person(S, 'UNDERWRITER').name",
        label: "Underwriter Name",
        type: "string",
      },
      {
        path: "person(S, 'UNDERWRITER').email",
        label: "Underwriter Email",
        type: "string",
      },
      {
        path: "person(S, 'UNDERWRITER').phone",
        label: "Underwriter Phone",
        type: "string",
      },
      {
        path: "person(S, 'UNDERWRITER').title",
        label: "Underwriter Title",
        type: "string",
      },
      {
        path: "person(S, 'UNDERWRITER').location",
        label: "Underwriter Location",
        type: "string",
      },
    ],
  },
  "Layer / Policy": {
    expanded: false,
    items: [
      { path: "L.reference", label: "Policy Number", type: "string" },
      { path: "L.policyForm", label: "Policy Form", type: "string" },
      { path: "L.policyType", label: "Policy Type", type: "string" },
      { path: "L.coverage", label: "Coverage", type: "string" },
      { path: "L.status", label: "Layer Status", type: "string" },
      { path: "L.paperProvider", label: "Paper Provider", type: "string" },
      { path: "L.triaStatus", label: "TRIA Status", type: "string" },
    ],
  },
  "Layer Limits & Attachment": {
    expanded: false,
    items: [
      { path: "L.limit", label: "Layer Limit", type: "number" },
      { path: "L.attachment", label: "Attachment Point", type: "number" },
      { path: "L.aggregateLimit", label: "Aggregate Limit", type: "number" },
      { path: "L.lineSize", label: "Line Size", type: "number" },
      { path: "L.shareOfLine", label: "Share of Line %", type: "number" },
      { path: "L.authorizedLimit", label: "Authorized Limit", type: "number" },
    ],
  },
  "Premium & Pricing": {
    expanded: false,
    items: [
      { path: "L.grossPremium", label: "Gross Premium", type: "number" },
      { path: "L.shareOfPremium", label: "Share of Premium", type: "number" },
      { path: "L.brokerage", label: "Brokerage %", type: "number" },
      { path: "L.expenses", label: "Expenses %", type: "number" },
      { path: "L.profit", label: "Profit %", type: "number" },
      { path: "L.TRIAAmount", label: "TRIA Premium", type: "number" },
      {
        path: "L.minimumEarnedPremium",
        label: "Minimum Earned Premium %",
        type: "number",
      },
      {
        path: "L.paymentTerms.days",
        label: "Payment Terms (Days)",
        type: "number",
      },
      {
        path: "S.program.brokerage",
        label: "Program Brokerage %",
        type: "number",
      },
      {
        path: "S.program.otherAcquisitionCosts",
        label: "Other Acquisition Costs %",
        type: "number",
      },
    ],
  },
  Tower: {
    expanded: false,
    items: [
      { path: "S.tower.layers", label: "Tower Layers (List)", type: "list" },
      {
        path: "S.tower.layers[].attachment",
        label: "Tower Layer Attachment",
        type: "number",
      },
      {
        path: "S.tower.layers[].limit",
        label: "Tower Layer Limit",
        type: "number",
      },
      {
        path: "S.tower.layers[].shares",
        label: "Tower Layer Shares (List)",
        type: "list",
      },
      {
        path: "S.tower.layers[].shares[].carrier",
        label: "Share Carrier",
        type: "string",
      },
      {
        path: "S.tower.layers[].shares[].shareOfGrossPremium",
        label: "Share of Gross Premium",
        type: "number",
      },
    ],
  },
  Endorsements: {
    expanded: false,
    items: [
      {
        path: "S.endorsements.endorsementsByLayer",
        label: "Endorsements by Layer (List)",
        type: "list",
      },
      { path: "AE", label: "Active Endorsement", type: "object" },
      {
        path: "T.endorsementIndex",
        label: "Endorsement Number",
        type: "number",
      },
    ],
  },
  Template: {
    expanded: false,
    items: [
      { path: "T.id", label: "Template ID", type: "string" },
      { path: "T.title", label: "Template Title", type: "string" },
      { path: "T.attributes", label: "Template Attributes", type: "object" },
    ],
  },
  "Followed Policy": {
    expanded: false,
    items: [
      {
        path: "L.followedPolicyTowerShareId",
        label: "Followed Policy Share ID",
        type: "string",
      },
      {
        path: "tower_share(S, followedId).carrier",
        label: "Followed Policy Carrier",
        type: "string",
      },
      {
        path: "tower_share(S, followedId).reference",
        label: "Followed Policy Reference",
        type: "string",
      },
      {
        path: "tower_share(S, followedId).coverage",
        label: "Followed Policy Coverage",
        type: "string",
      },
      {
        path: "tower_share(S, followedId).policyForm",
        label: "Followed Policy Form",
        type: "string",
      },
      {
        path: "tower_share(S, followedId).layer.inception",
        label: "Followed Policy Inception",
        type: "date",
      },
      {
        path: "tower_share(S, followedId).layer.expiration",
        label: "Followed Policy Expiration",
        type: "date",
      },
      {
        path: "tower_share(S, followedId).layer.limit",
        label: "Followed Policy Limit",
        type: "number",
      },
      {
        path: "tower_share(S, followedId).layer.attachment",
        label: "Followed Policy Attachment",
        type: "number",
      },
    ],
  },
  "Prior Submission": {
    expanded: false,
    items: [
      {
        path: "prior_submission(S)",
        label: "Prior Submission",
        type: "object",
      },
      { path: "prior_layer(S, L)", label: "Prior Layer", type: "object" },
      {
        path: "S.program.priorSubmission",
        label: "Prior Submission Reference",
        type: "string",
      },
    ],
  },
  Exposure: {
    expanded: false,
    items: [
      {
        path: "latest_exposure(S, 'CONSTRUCTION_VALUE')",
        label: "Construction Value",
        type: "number",
      },
      {
        path: "latest_exposure(S, 'TIV')",
        label: "Total Insured Value (TIV)",
        type: "number",
      },
      {
        path: "S.state.input.exposure.data",
        label: "Exposure Data",
        type: "object",
      },
    ],
  },
};

export const ANNOTATION_TOOLS = [
  { id: "select", icon: SelectIcon, label: "Select", color: "#666666" },
  { id: "circle", icon: CircleIcon, label: "Circle", color: "#e53935" },
  { id: "line", icon: LineIcon, label: "Line", color: "#1e88e5" },
  { id: "arrow", icon: ArrowIcon, label: "Arrow", color: "#43a047" },
  { id: "box", icon: BoxIcon, label: "Rectangle", color: "#fb8c00" },
  {
    id: "highlight",
    icon: HighlightIcon,
    label: "Highlight",
    color: "#fdd835",
  },
  { id: "comment", icon: CommentIcon, label: "Comment", color: "#8e24aa" },
];
